import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { isEmpty } from 'lodash';
import { compose, lifecycle, withHandlers, withState } from 'recompose';
import { Expand } from '@sparefoot/react-core';
import { formatUSD } from '@sparefoot/react-core/utils/format';
import { scrollToElement } from '@sparefoot/react-core/utils/scrolling';

import { Paragraph, Text } from 'components/core/Typography';
import { Button } from 'components/core/Button';
import { UnitReservationForm } from 'components/unit/UnitReservationForm';
import { FeeWarningToolTip } from 'components/unit/FeeWarningToolTip';
import { UnitIcon } from 'components/unit/UnitIcon';
import { ExpressBanner } from 'components/core/ExpressBanner';

import './BookableUnitDetails.scss';
import { TrackingClient } from '@sparefoot/tracking-client';
import { trackCTAClick } from '../../core/library/tracking-utils/tracking/clickTrackingUtils';

export const enhance = compose(
	withState('instance', 'setRef'),
	withHandlers({
		handleExpandUnit: (props) => () => {
			const { unit, facility, isExpanded } = props;
			if (!isExpanded) {
				trackCTAClick(
					'render-reservation-form-button',
					'Continue Button',
					'Continue',
					props.url,
				);

				TrackingClient.trackFacilityUnitSelected({
					facility: {
						id: facility?.id || 'Unknown Facility ID',
						name: facility?.name || 'Unknown Facility',
						location: `${facility?.location?.city || 'Unknown City'}, ${facility?.location?.state || 'Unknown State'}`,
					},
					last_unit_added_date: new Date(),
					unit: {
						currency: 'usd',
						id: unit.id,
						variant_size: unit?.sizeText || 'Unknown Size',
						price: unit?.price?.sparefoot || 'Unknown Price',
						quantity: 1,
					},
					unit_id: unit.id,
					unit_promo: unit.promotion || 'No Promo',
				});
			}
			return props.onExpandUnit(props.unit.id, props.storageType);
		},
	}),
	lifecycle({
		componentDidUpdate(prevProps) {
			if (this.props.isExpanded && !prevProps.isExpanded) {
				// scroll after expand animation
				setTimeout(
					() =>
						scrollToElement(
							this.props.instance,
							{ offset: -84 }, // sticky search height
						),
					350,
				);
			}
		},
	}),
);

export function BookableUnitDetails({
	facility,
	handleExpandUnit,
	isExpanded,
	onCreateBooking,
	unit,
	setRef,
}) {
	const showOMI = facility.hasOnlineMoveins;
	const tierAndType = unit.tierName
		? `${unit.tierName} ${unit.typeText}`
		: unit.typeText;

	return (
		<div className="bookable-unit-details">
			<div
				className="unit-details"
				ref={setRef}
			>
				<div className="unit-image-and-description">
					{/* Unit Image */}
					<div className="facility-unit-image">
						<UnitIcon
							squareFootage={unit.squareFootage}
							type={unit.unitType}
							amenities={unit.amenities}
							alt={`Depiction of a ${unit.size} self-storage unit`}
						/>
					</div>

					<div className="facility-unit-size">
						<Text
							last
							size="medium"
							className="unit-size"
							weight="extraBold"
						>
							{unit.sizeText}{' '}
							<Text
								size="medium"
								weight="standard"
							>
								{tierAndType}
							</Text>
						</Text>

						{/* Unit Description */}
						{unit?.tierDescription && (
							<Text
								size="tiny"
								weight="extraBold"
								color="darkGray"
								className="unit-description"
							>
								Unit Description:
								<Text
									size="tiny"
									color="gray"
								>
									{unit.tierDescription}
								</Text>
							</Text>
						)}

						<div className="amenities-list">
							{!!unit.special && (
								<Paragraph
									last
									size="tiny"
									className="unit-promo"
									color="greenHero"
								>
									{unit.special}
								</Paragraph>
							)}
							{!isEmpty(unit.availableAmenities) && (
								<ul className="amenities">
									{unit.availableAmenities.map(
										(amenity, index) => (
											// Kind of nasty logic to add the commas in the amenities list, potentially refactor?
											<li key={amenity}>
												<Text
													size="tiny"
													color="gray"
												>
													{amenity}
													{index <
														unit.availableAmenities
															.length -
															1 && ','}
												</Text>
											</li>
										),
									)}
								</ul>
							)}
						</div>
					</div>
				</div>

				<div className="facility-price-reserve-container">
					<div className="facility-unit-price">
						{unit.quantity && unit.quantity <= 3 ? (
							<Fragment>
								<Text
									last
									size="tiny"
									color="red"
								>
									{unit.quantity} left at
								</Text>
								<Text
									last
									color="red"
									size="mediumLarge"
									className="unit-price"
								>
									{formatUSD(unit.price.meta)}
								</Text>
							</Fragment>
						) : (
							<Text
								last
								size="mediumLarge"
								className="unit-price"
							>
								{formatUSD(unit.price.meta)}
							</Text>
						)}
						<Text
							last
							color="red"
							className="unit-price-frequency"
						>
							per month
						</Text>
						{facility.showFeeWarning && facility.totalFees > 0 && (
							<FeeWarningToolTip
								facility={facility}
								className="fee-tooltip"
							/>
						)}
					</div>

					<div className="facility-unit-reserve">
						<Button
							primary
							className={classnames('reserve-button', {
								gray: isExpanded,
							})}
							onClick={handleExpandUnit}
						>
							{!isExpanded ? 'Continue' : 'X'}
						</Button>
					</div>
				</div>
			</div>
			<Expand showing={isExpanded}>
				<div className="reservation-form-wrapper">
					<div className="reservation-form-container">
						{showOMI ? (
							<div className="express-banner">
								<ExpressBanner last />
							</div>
						) : (
							<Paragraph className="lock-in">
								Lock in this rate now. No credit card, no
								obligation.
							</Paragraph>
						)}
						<UnitReservationForm
							onSubmit={onCreateBooking}
							unit={unit}
							facility={facility}
						/>
					</div>
				</div>
			</Expand>
		</div>
	);
}

BookableUnitDetails.propTypes = {
	setRef: PropTypes.func,
	facility: PropTypes.object.isRequired,
	handleExpandUnit: PropTypes.func.isRequired,
	isExpanded: PropTypes.bool.isRequired,
	onCreateBooking: PropTypes.func.isRequired,
	unit: PropTypes.object.isRequired,
};

export default enhance(BookableUnitDetails);
